import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'
import { supabase } from './supabase'

/**
 * Sentry integration for the opdrachtgever portal.
 *
 * Captures:
 *  - Unhandled errors & crashes (Vue render errors + global window handlers)
 *  - Performance / tracing (page loads, navigations, fetch/XHR)
 *  - Session replay (always sampled on errors, lightly sampled otherwise)
 *
 * Sentry only initialises when `VITE_SENTRY_DSN` is set, so local development
 * stays quiet unless a DSN is explicitly provided.
 */

const dsn = import.meta.env.VITE_SENTRY_DSN

export const isSentryEnabled = Boolean(dsn)

function sampleRate(value: string | undefined, fallback: number): number {
  const parsed = Number.parseFloat(value ?? '')
  return Number.isFinite(parsed) ? parsed : fallback
}

export function initSentry(app: App, router: Router): void {
  if (!isSentryEnabled) {
    if (import.meta.env.DEV) {
      console.info('[Sentry] disabled — set VITE_SENTRY_DSN to enable error tracking')
    }
    return
  }

  Sentry.init({
    app,
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,

    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        // Don't leak text/inputs from the opdrachtgever portal into replays.
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],

    // Performance monitoring — sample rate is tunable per environment.
    tracesSampleRate: sampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0.2),
    // Only trace requests to our own backends.
    tracePropagationTargets: [/^\//, /supabase\.inspeqt(-dev)?\.net/, /db\.inspeqt\.net/, /api\.inspeqt(-dev)?\.net/],

    // Session replay — capture a small fraction of normal sessions and
    // every session that produces an error.
    replaysSessionSampleRate: sampleRate(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE, 0.1),
    replaysOnErrorSampleRate: sampleRate(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE, 1.0),

    // Keep PII attached to events so we can correlate to a logged-in user.
    sendDefaultPii: true,

    // Noise that reaches Sentry via the global unhandled-rejection handler and
    // therefore can't be filtered in captureError below. The navigator-lock
    // AbortError comes from @supabase/gotrue-js when the cross-tab auth lock
    // times out (e.g. multiple portal tabs) — transient and self-recovering.
    // "browsing context is going away" is the AbortError the browser uses to
    // reject still-pending DOM promises (gotrue's navigator.locks request, an
    // in-flight fetch) when the tab is closed, reloaded or navigated away.
    // Typical case (PORTAL-VUE-10): iOS Safari resumes a portal tab that sat
    // in the background, gotrue's auto token refresh fails with "Load failed"
    // and the page is discarded/reloaded mid-request. The page is being torn
    // down, so there is nothing to recover and nothing actionable.
    ignoreErrors: [
      'signal is aborted without reason',
      'AbortError: signal is aborted',
      /browsing context is going away/i,
    ],
  })

  // Tag every event so portal issues are easy to separate from the
  // inspector frontend if it is instrumented later.
  Sentry.setTag('app', 'portal')

  bindSupabaseUser()
}

/**
 * Mirror the current Supabase auth user onto Sentry so events and replays are
 * attributed to the right opdrachtgever, and cleared on logout.
 */
function bindSupabaseUser(): void {
  supabase.auth
    .getSession()
    .then(({ data }) => applyUser(data.session?.user ?? null))
    .catch(() => {
      /* getSession failures are surfaced elsewhere; ignore for Sentry context */
    })

  supabase.auth.onAuthStateChange((_event, session) => {
    applyUser(session?.user ?? null)
  })
}

function applyUser(user: { id: string; email?: string } | null): void {
  if (!isSentryEnabled) return

  if (user) {
    Sentry.setUser({ id: user.id, email: user.email })
  } else {
    Sentry.setUser(null)
  }
}

/**
 * Errors that are expected in normal operation (flaky mobile networks, auth
 * rate limits, expired one-time codes, aborted cross-tab auth locks). They are
 * already surfaced to the user with a toast/message; reporting them to Sentry
 * only buries real defects. Matched against both the error's name/code and its
 * message so it works for supabase-js error classes and plain objects alike.
 */
function isExpectedError(error: unknown): boolean {
  const name = getErrorField(error, 'name')
  const code = getErrorField(error, 'code')
  const message = getErrorField(error, 'message')

  // Client couldn't reach our backend at all — connectivity, not a bug.
  if (name === 'FunctionsFetchError' || name === 'AuthRetryableFetchError') return true
  if (/failed to send a request to the edge function/i.test(message)) return true
  if (/failed to fetch|load failed|networkerror when attempting/i.test(message)) return true

  // gotrue navigator-lock contention (multiple tabs) aborts with this signal.
  // Also matches pending promises rejected because the page is unloading
  // ("browsing context is going away").
  if (name === 'AbortError' || /signal is aborted|browsing context is going away/i.test(message)) return true

  // GoTrue rate limit when the user re-requests a login link/code too quickly.
  if (code === 'over_request_rate_limit' || /you can only request this after \d+ seconds/i.test(message)) return true

  // The user pasted an expired or already-used one-time code / magic link.
  if (code === 'otp_expired' || /token has expired or is invalid/i.test(message)) return true

  return false
}

function getErrorField(error: unknown, field: 'name' | 'code' | 'message'): string {
  if (error && typeof error === 'object' && field in error) {
    const value = (error as Record<string, unknown>)[field]
    if (typeof value === 'string') return value
  }
  return ''
}

/**
 * Supabase (PostgREST/storage) errors are often plain `{ code, details, hint,
 * message }` objects. Capturing those directly gives Sentry no stack trace and
 * a garbage title (the minified name of the capturing function). Wrap them in
 * a real Error so issues are titled by the actual message.
 */
function normalizeError(error: unknown): { error: unknown; extra?: Record<string, unknown> } {
  if (error instanceof Error) return { error }
  if (error && typeof error === 'object') {
    const message = getErrorField(error, 'message')
    if (message) {
      const wrapped = new Error(message)
      wrapped.name = getErrorField(error, 'name') || getErrorField(error, 'code') || 'ApplicationError'
      return { error: wrapped, extra: { originalError: error } }
    }
  }
  return { error }
}

/**
 * Manually report a handled error. Use this in `catch` blocks where the error
 * is swallowed (logged + toast) but should still reach Sentry.
 *
 * Expected/transient errors (network failures, auth rate limits, expired
 * codes) are recorded as breadcrumbs instead of exceptions.
 */
export function captureError(error: unknown, context?: Record<string, unknown>): void {
  if (!isSentryEnabled) return

  if (isExpectedError(error)) {
    Sentry.addBreadcrumb({
      category: 'expected-error',
      level: 'warning',
      message: getErrorField(error, 'message') || String(error),
      data: context,
    })
    return
  }

  const normalized = normalizeError(error)
  const extra = { ...normalized.extra, ...context }
  Sentry.captureException(normalized.error, Object.keys(extra).length ? { extra } : undefined)
}
